import $ from 'jquery';
import {sprintf} from '@wordpress/i18n';
import {
    isProduction,
    setFieldValue,
    isFieldRequired,
    isValidAddress,
    getFieldValue,
    ConfigurationError
} from "@ppcp/utils";
import BaseGateway from './BaseGateway';

/**
 * Google Pay Gateway Implementation
 *
 * Handles Google Pay-specific button creation and configuration
 */
class GooglePayGateway extends BaseGateway {

    static BASE_REQUEST = {
        allowedPaymentMethods: []
    }

    constructor(props) {
        super(props);
        this.paymentsClient = null;
        this.googlePayConfig = null;
        this.capabilities.push('form_validation'); // Google Pay needs form validation on checkout
        this.isReady = null; // Cache the ready state for performance
        this.$button = null;
        this.promises = [];
    }

    /**
     * Check if Google Pay can make payments
     * @returns {Promise<boolean>} True if Google Pay is available and ready
     * @throws {ConfigurationError} When there's a configuration issue (SDK not loaded, currency unsupported)
     */
    async canMakePayment() {
        // Configuration issues - throw error to show message to user/admin
        if (!this.paypal?.Googlepay) {
            return false;
        }
        if (!this.settings.supported_currencies.includes(this.controller.getCurrency())) {
            return false;
        }

        // Check browser/device support
        if (!this.paymentsClient) {
            try {
                await this.createPaymentsClient();
            } catch (error) {
                if (error?.errorName === 'GOOGLEPAY_CONFIG_ERROR') {
                    throw new ConfigurationError(error.message);
                }
                throw new ConfigurationError(error.message);
            }
        }

        // Browser unsupported - return false (no message, just hide)
        return await this.isReadyToPay();
    }

    /**
     * Check if Google Pay button should be created
     * @param {HTMLElement|NodeList} container - The container to check
     * @returns {boolean} True if button doesn't already exist
     */
    shouldCreateElement(container) {
        return !$(container).find('button.wc-ppcp_googlepay-button').length;
    }

    /**
     * Create Google Pay button in the specified container
     * @param {HTMLElement|NodeList} container - The container element(s) to render button
     * @returns {Object} Object containing button instance or null
     */
    async createPaymentElement(container) {
        try {
            if (!this.paymentsClient) {
                await this.createPaymentsClient();
            }

            if (await this.isReadyToPay()) {
                const button = this.paymentsClient.createButton({
                    onClick: this.onButtonClick.bind(this),
                    ...this.getButtonOptions()
                    //allowedPaymentMethods: this.googlePayConfig.allowedPaymentMethods
                });

                if (this.controller.context.isCheckout()) {
                    button.addEventListener('click', this.validateCheckout.bind(this), true);
                }

                $(button).find('button')
                    .addClass(`wc-ppcp_googlepay-button ${this.controller.context.getPage()}-context`)
                    .attr('id', `gpay-button-online-api-id-${this.controller.context.getPage()}`)
                    .css({
                        height: this.settings.button.buttonHeight
                    });
                container.appendChild(button);

                this.$button = $(button);
                return container;
            }
        } catch (error) {
            console.error('Failed to create Google Pay button:', error);
            return null;
        }
    }

    async validateCheckout(e) {
        if (this.$button.is('.skip-validation')) {
            this.$button.removeClass('skip-validation');
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        $(e.target).prop('disabled', true);

        const result = await this.controller.validate();
        $(e.target).prop('disabled', false);
        if (!result) {
            return;
        }
        this.$button.addClass('skip-validation');
        $(e.target).trigger('click');
    }

    async createPaymentsClient() {
        try {
            await this.initializeConfig();
            this.paymentsClient = new google.payments.api.PaymentsClient(this.buildPaymentsClientRequest());
        } catch (error) {
            throw error;
        }
    }

    async initializeConfig() {
        if (!this.googlePayConfig) {
            this.googlePayConfig = await this.paypal.Googlepay().config();
        }
    }

    buildPaymentsClientRequest() {
        const {
            merchantInfo
        } = this.googlePayConfig;

        const shouldRequestShipping = this.controller.needsShipping() && !this.controller.context.isCheckout();

        return {
            environment: isProduction() ? 'PRODUCTION' : 'TEST',
            merchantInfo: {
                ...merchantInfo
            },
            paymentDataCallbacks: {
                onPaymentAuthorized: async (response) => this.onPaymentAuthorized(response),
                ...(shouldRequestShipping && {
                    onPaymentDataChanged: (response) => this.onPaymentDataChanged(response)
                })
            }
        };
    }

    async isReadyToPay() {
        // Return cached result if available
        if (this.isReady !== null) {
            return this.isReady;
        }

        const {
            apiVersion,
            apiVersionMinor,
            allowedPaymentMethods
        } = this.googlePayConfig;

        const response = await this.paymentsClient.isReadyToPay({
            apiVersion,
            apiVersionMinor,
            allowedPaymentMethods,
            existingPaymentMethodRequired: false
        });

        this.isReady = !!response.result;
        return this.isReady;
    }

    /**
     * Method that is called when the GPay button is clicked.
     */
    async onButtonClick(e) {
        try {
            this.controller.onClick();

            await this.createPaymentsClient();

            if (this.controller.context.isProduct()) {
                this.promises.push(this.controller.addToCart().then(orderId => {
                    this.orderId = orderId;
                }));
                if (!this.controller.needsShipping()) {
                    try {
                        this.disablePaymentElement();
                        await Promise.all(this.promises);
                    } catch (error) {
                        console.log('Error adding item to cart. ', error);
                    } finally {
                        this.enablePaymentElement();
                    }
                }
            }

            const paymentData = await this.paymentsClient.loadPaymentData(this.buildPaymentDataRequest());

            // Check if 3DS is required after wallet closes
            if (this.confirmOrderResponse?.status === 'PAYER_ACTION_REQUIRED') {
                await this.initiatePayerAction(this.confirmOrderResponse.orderId);
            }

            // Extract standardized data and pass to submit
            const standardizedData = this.extractStandardizedCheckoutData(paymentData);
            this.controller.submit(standardizedData);
        } catch (error) {
            if (error?.statusCode === 'CANCELED') {
                this.controller.onCancel();
                return;
            }
            this.controller.submitError(error?.statusMessage || error);
        } finally {
            this.promises = [];
        }
    }

    async onPaymentAuthorized(data) {
        try {
            // create the order.
            let orderId;

            if (this.orderId) {
                orderId = this.orderId;
            } else {
                orderId = await this.controller.createOrder();
            }

            let response = await this.paypal.Googlepay().confirmOrder({
                orderId,
                paymentMethodData: data.paymentMethodData
            });

            // Store response for handling after wallet closes
            this.confirmOrderResponse = {
                status: response.status,
                orderId
            };

            if (['APPROVED', 'PAYER_ACTION_REQUIRED'].includes(response.status)) {
                setFieldValue(this.getOrderFieldKey(), orderId, null);
                return {
                    transactionState: 'SUCCESS'
                }
            } else {
                return {
                    transactionState: 'error',
                    error: {
                        intent: 'PAYMENT_AUTHORIZATION',
                        message: ''
                    }
                }
            }
        } catch (error) {
            if (error?.code === 'validation_errors') {
                return {
                    transactionState: 'error',
                    error: {
                        intent: 'PAYMENT_AUTHORIZATION',
                        message: error.data.sanitized_errors.join(' ')
                    }
                }
            }
            return {
                transactionState: 'error',
                error: {
                    intent: 'PAYMENT_AUTHORIZATION',
                    message: error?.message
                }
            }
        } finally {
            this.orderId = null;
        }
    }

    async onPaymentDataChanged(data) {
        try {
            if (this.promises.length) {
                await Promise.all(this.promises);
                this.promises = [];
            }
            // Extract and format shipping data from Google Pay's structure
            const shippingAddress = this.formatShippingAddress(data.shippingAddress);
            let shippingOption = data.shippingOptionData?.id;
            // Google Pay sends 'shipping_option_unselected' as placeholder when no option selected
            if (shippingOption === 'shipping_option_unselected') {
                shippingOption = null;
            }

            // Call cart's updateShipping with formatted data
            const result = await this.controller.onShippingChange({
                address: shippingAddress,
                shipping_method: shippingOption
            });

            // Return Google Pay-formatted response
            return this.formatPaymentDataResponse(result);
        } catch (error) {
            // WordPress REST API errors come with structured error data
            // The CartShipping endpoint returns errors in format:
            // { code: 'shipping_error', message: '...', data: { status: 400, error: {...} } }
            if (error?.data?.error) {
                return {
                    error: {
                        reason: error.data.error.reason || 'SHIPPING_ADDRESS_INVALID',
                        message: error.data.error.message || error.message,
                        intent: error.data.error.intent || 'SHIPPING_ADDRESS'
                    }
                };
            }

            // Handle unexpected errors
            return {
                error: {
                    reason: 'OTHER_ERROR',
                    message: error?.message || 'Unable to calculate shipping for this address',
                    intent: 'SHIPPING_ADDRESS'
                }
            };
        }
    }

    async initiatePayerAction(orderId) {
        return this.paypal.Googlepay().initiatePayerAction({orderId});
    }

    /**
     * Format Google Pay address to WooCommerce format
     * @param {Object} address - Google Pay shipping address
     * @returns {Object} WooCommerce formatted address
     */
    formatShippingAddress(address) {
        return {
            postcode: address.postalCode,
            city: address.locality,
            state: address.administrativeArea,
            country: address.countryCode
        };
    }

    /**
     * Format cart response to Google Pay's expected response format
     * @param {Object} cartResponse - Response from cart.updateShipping()
     * @returns {Object} Google Pay formatted payment data response
     */
    formatPaymentDataResponse(cartResponse) {
        return {
            newTransactionInfo: {
                totalPrice: cartResponse.total,
                totalPriceStatus: 'FINAL',
                countryCode: cartResponse.countryCode,
                currencyCode: cartResponse.currency,
                displayItems: this.mapLineItemsToGooglePay(cartResponse.lineItems || [])
            },
            ...(cartResponse.shippingOptions && {
                newShippingOptionParameters: {
                    shippingOptions: this.mapShippingOptionsToGooglePay(cartResponse.shippingOptions),
                    ...(cartResponse.selectedShippingMethod && {
                        defaultSelectedOptionId: cartResponse.selectedShippingMethod
                    })
                }
            })
        };
    }

    /**
     * Check if shipping address is valid on checkout page
     * @returns {boolean} True if address is valid
     */
    hasValidShippingAddress() {
        if (!this.controller.context.isCheckout()) {
            return false;
        }

        const shippingAddress = {
            first_name: getFieldValue('shipping_first_name'),
            last_name: getFieldValue('shipping_last_name'),
            address_1: getFieldValue('shipping_address_1'),
            address_2: getFieldValue('shipping_address_2'),
            city: getFieldValue('shipping_city'),
            state: getFieldValue('shipping_state'),
            postcode: getFieldValue('shipping_postcode'),
            country: getFieldValue('shipping_country')
        };

        return isValidAddress(shippingAddress);
    }

    buildPaymentDataRequest() {
        const {
            merchantInfo,
            apiVersion,
            apiVersionMinor,
            allowedPaymentMethods
        } = this.googlePayConfig;

        const context = this.controller.context;

        // When context is "checkout" the customer will manually enter their billing details on the
        // checkout page
        if (context.isCheckout()) {
            if (allowedPaymentMethods[0]?.parameters?.billingAddressRequired) {
                allowedPaymentMethods[0].parameters.billingAddressRequired = false;
            }
        }

        // Set phoneNumberRequired based on WooCommerce field requirements
        if (allowedPaymentMethods[0]?.parameters?.billingAddressParameters) {
            allowedPaymentMethods[0].parameters.billingAddressParameters.phoneNumberRequired = isFieldRequired('billing_phone');
        }

        // Determine if we need to request shipping info from Google Pay
        // On checkout, only request shipping info if it's the express checkout button.
        const shouldRequestShipping = this.controller.needsShipping() && !context.isCheckout();

        const request = {
            ...this.constructor.BASE_REQUEST,
            merchantInfo: {
                ...merchantInfo
                //merchantName: this.settings.merchant_name
            },
            apiVersion,
            apiVersionMinor,
            allowedPaymentMethods,
            transactionInfo: {
                countryCode: this.settings.country_code,
                currencyCode: this.controller.getCurrency(),
                totalPriceStatus: 'FINAL',
                totalPrice: this.controller.getTotal(),
                displayItems: this.mapLineItemsToGooglePay(this.controller.getLineItems()),
                totalPriceLabel: this.settings.i18n.total_price_label
            },
            emailRequired: isFieldRequired('billing_email'),
            ...(shouldRequestShipping && {
                shippingAddressParameters: {},
                shippingAddressRequired: true,
                shippingOptionRequired: true,
                ...(this.controller.getShippingOptions().length > 0 && {
                    shippingOptionParameters: {
                        shippingOptions: this.mapShippingOptionsToGooglePay(this.controller.getShippingOptions()),
                        ...(this.controller.getSelectedShippingMethod() && {
                            defaultSelectedOptionId: this.controller.getSelectedShippingMethod()
                        })
                    }
                })
            }),
            callbackIntents: [
                'PAYMENT_AUTHORIZATION',
                ...(shouldRequestShipping ? ['SHIPPING_ADDRESS', 'SHIPPING_OPTION'] : [])
            ]
        };

        return this.actions.applyFilters('googlepay_payment_request', request, this);
    }

    disablePaymentElement() {
        const context = this.controller.context;
        if (context.isOrderPay()) {
            return;
        }
        this.$button?.find('button').prop('disabled', true);
        this.$button?.addClass('disabled');
    }

    enablePaymentElement() {
        this.$button?.find('button').prop('disabled', false);
        this.$button?.removeClass('disabled');
    }

    destroyPaymentElement() {
        this.$button?.remove();
    }

    /**
     * Extract standardized checkout data from Google Pay payment response
     * @param {Object} paymentData - Google Pay payment data
     * @returns {Object} Standardized checkout data with billing and shipping properties
     */
    extractStandardizedCheckoutData(paymentData) {
        const data = {};

        // Extract billing data from paymentMethodData
        if (paymentData?.paymentMethodData?.info) {
            const billing = {};
            const info = paymentData.paymentMethodData.info;

            // Add billing address if available
            if (info.billingAddress) {
                const address = info.billingAddress;
                billing.first_name = address.name?.split(' ')[0] || '';
                billing.last_name = address.name?.split(' ').slice(1).join(' ') || '';
                billing.address_1 = address.address1 || '';
                billing.address_2 = address.address2 || '';
                billing.city = address.locality || '';
                billing.state = address.administrativeArea || '';
                billing.postcode = address.postalCode || '';
                billing.country = address.countryCode || '';
                billing.phone = address.phoneNumber || ''
            }

            // Add email if available
            if (paymentData.email) {
                billing.email = paymentData.email;
            }

            data.billing = billing;
        }

        // Extract shipping data
        if (paymentData?.shippingAddress) {
            const shipping = {};
            const address = paymentData.shippingAddress;

            shipping.first_name = address.name?.split(' ')[0] || '';
            shipping.last_name = address.name?.split(' ').slice(1).join(' ') || '';
            shipping.address_1 = address.address1 || '';
            shipping.address_2 = address.address2 || '';
            shipping.city = address.locality || '';
            shipping.state = address.administrativeArea || '';
            shipping.postcode = address.postalCode || '';
            shipping.country = address.countryCode || '';

            // Add phone if available
            if (address.phoneNumber) {
                shipping.phone = address.phoneNumber;
            }

            data.shipping = shipping;
        }

        return data;
    }

    /**
     * Get Google Pay button styling options
     * @returns {Object} Button styling configuration
     */
    getButtonOptions() {

        return {
            buttonBorderType: this.settings.button.buttonBorderType,
            buttonColor: this.settings.button.buttonColor,
            buttonType: this.settings.button.buttonType,
            buttonRadius: this.settings.button.buttonRadius,
            buttonLocale: this.settings.button.buttonLocale,
            buttonSizeMode: this.settings.button.buttonSizeMode,
            ...(this.controller.context.isExpressCheckout() && {
                buttonSizeMode: 'fill'
            })
        };
    }

    /**
     * Map generic line items to Google Pay display items format
     * @param {Array} lineItems - Generic line items from controller
     * @returns {Array} Google Pay formatted display items
     */
    mapLineItemsToGooglePay(lineItems) {
        return lineItems.map(item => {
            // Map generic type to Google Pay type
            let type = 'LINE_ITEM';
            if (item.type === 'tax') {
                type = 'TAX';
            } else {
                type = 'SUBTOTAL';
            }

            return {
                label: item.label,
                type: type,
                price: item.amount
            };
        });
    }

    /**
     * Map generic shipping options to Google Pay shipping options format
     * @param {Array} shippingOptions - Generic shipping options from controller
     * @returns {Array} Google Pay formatted shipping options
     */
    mapShippingOptionsToGooglePay(shippingOptions) {
        return shippingOptions
            .sort((a, b) => {
                return parseFloat(a.amount) - parseFloat(b.amount);
            })
            .map(option => ({
                id: option.id,
                label: sprintf('%s: %s %s', option.label, option.amount, this.controller.getCurrency()),
                description: option.description || ''
            }));
    }

}


export {GooglePayGateway};
export default GooglePayGateway;